import React, { useState, useEffect } from "react";
import NameInfo from "./NameInfo.jsx";
import PageHeader from "./PageHeader.jsx";

function getRankMessage(progress) {
  if (progress < 10) {
    return "Just getting started!";
  } else if (progress < 20) {
    return "Great start!";
  } else if (progress < 30) {
    return "You're picking up steam!";
  } else if (progress < 40) {
    return "Great momentum!";
  } else if (progress < 50) {
    return "Almost halfway there!";
  } else if (progress < 60) {
    return "Solid progress!";
  } else if (progress < 70) {
    return "Looking good!";
  } else if (progress < 90) {
    return "Great work!";
  } else if (progress < 80) {
    return "Getting closer!";
  } else if (progress < 100) {
    return "Almost there!";
  } else if (progress < 110) {
    return "Great job! You can keep ranking if you'd like, but there's plenty of data.";
  } else if (progress < 120) {
    return "Bonus points!";
  } else if (progress < 130) {
    return "Overachiever alert!";
  } else if (progress < 140) {
    return "You’re on fire!";
  } else if (progress < 150) {
    return "Maxing it out!";
  } else {
    return "Wow! That's a lot of battling! That should be plenty to sort your names.";
  }
}


export default function HeadToHead({ user }) {
  const [pair, setPair] = useState(null);
  const [rankProgress, setRankProgress] = useState(null);
  const [metaA, setMetaA] = useState(null);
  const [metaB, setMetaB] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const load = async () => {
    setIsLoading(true);
    const res = await fetch(`/api/name/head-to-head/${user.username}`);
    const progressRes = await fetch(`/api/name/head-to-head-progress/${user.username}`);

    const data = await res.json();
    const progress = await progressRes.json();
    setRankProgress(Math.round(progress * 100));
    setPair(data);
    if (data.a) {
      const ma = await fetch(`/api/name/meta/${data.a}`).then(r => r.json());
      setMetaA(ma);
    }
    if (data.b) {
      const mb = await fetch(`/api/name/meta/${data.b}`).then(r => r.json());
      setMetaB(mb);
    }
    setIsLoading(false);
  };

  useEffect(() => { load(); }, []);

  // Keyboard support for choosing names
  useEffect(() => {
    const handleKeyPress = event => {
      if (!pair || isLoading) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        pick(pair.a);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        pick(pair.b);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        cantDecide();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [pair, isLoading]);

  const pick = async winner => {
    const loser = winner === pair.a ? pair.b : pair.a;
    setIsLoading(true);
    await fetch(`/api/name/head-to-head/${user.username}`, {
      method: 'POST',
      headers:{'Content-Type':'application/json'},
      body: JSON.stringify({ winner, loser })
    });
    load();
  };

  const cantDecide = async () => {
    setIsLoading(true);
    await fetch(`/api/name/head-to-head/${user.username}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ decision: 'tie', winner: pair.a, loser: pair.b })
    });
    load();
  };

  if (isLoading) {
    return (
      <div className="page">
        <div className="loading">Preparing your next matchup...</div>
      </div>
    );
  }

  if (!pair || !pair.a || !pair.b) {
    return (
      <div className="page">
        <PageHeader
          title="Need More Names! 📝"
          description="Add more names to your favorites in 'In or Out' to start head-to-head comparisons."
        />
      </div>
    );
  }

  return (
    <div className="page head2head-page">
      <PageHeader
        title="Head to Head"
        description="Choose your favorite! Use ← → arrow keys or click the buttons to pick."
      />
      {rankProgress !== null && (
        <div className="progress-section">
          <div
            className="progress-bar"
            role="progressbar"
            aria-valuenow={rankProgress}
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <div className="progress-bar-fill" style={{ width: `${rankProgress}%` }} />
          </div>
          <div className="progress-message">
            You're {rankProgress}% of the way to high quality rankings. {getRankMessage(rankProgress)}
          </div>
        </div>
      )}
      {/* Mobile-only: pick either name (or punt) without scrolling past the first card */}
      <div className="h2h-mobile-actions">
        <button className="h2h-choice-btn" onClick={() => pick(pair.a)}>
          👑 {pair.a}
        </button>
        <button className="h2h-choice-btn" onClick={() => pick(pair.b)}>
          👑 {pair.b}
        </button>
        <button className="h2h-tie-btn btn-secondary" onClick={cantDecide}>
          🤷 Can't Decide
        </button>
      </div>
      <div className="head2head-pair">
        <div className="head2head-name">
          <button className="pick-name-button" onClick={() => pick(pair.a)}>
            👑 Choose {pair.a}
          </button>
          <NameInfo name={pair.a} meta={metaA} gender={user.gender} showPctToggle={true} />
        </div>
        <div className="vs-container">
          <div className="vs-divider">
            VS
          </div>
          <button className="cant-decide-button" onClick={cantDecide}>
            Can't Decide
          </button>
        </div>
        <div className="head2head-name">
          <button className="pick-name-button" onClick={() => pick(pair.b)}>
            👑 Choose {pair.b}
          </button>
          <NameInfo name={pair.b} meta={metaB} gender={user.gender} showPctToggle={true} />
        </div>
      </div>
    </div>
  );
}
